import React, { useState, useMemo } from "react";
import { Modal, Table, Input, Button, Empty } from "antd";
import { SearchOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import MediaFetcher from "../library/MediaFetcher";
import { filterAdGroupsByMediaCompatibility } from "../utils/mediaUtils";
import moment from "moment";
import { modal as modalTokens } from "../../Tasks/configs/designTokens";

const AdUnitAssignmentModal = ({
  open,
  onCancel,
  selectedAdUnits,
  availableAdGroups,
  selectedAdGroupIds,
  setSelectedAdGroupIds,
  onAssign,
  onCreateGroup,
  assigning,
  mediaType = "IMAGE",
}) => {
  const [search, setSearch] = useState("");

  const filteredAdGroups = useMemo(() => {
    const compatible = filterAdGroupsByMediaCompatibility(availableAdGroups, mediaType);
    if (!search.trim()) return compatible;
    const q = search.trim().toLowerCase();
    return compatible.filter(g => g.name.toLowerCase().includes(q));
  }, [availableAdGroups, mediaType, search]);

  const selectedCount = selectedAdGroupIds?.length || 0;
  const isVideo = mediaType === "VIDEO";

  const adGroupColumns = [
    {
      title: `${isVideo ? "Video" : "Display"} Ad Group`,
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Ad Units",
      dataIndex: "medias",
      key: "medias",
      width: 80,
      align: "center",
      render: (_, record) => record.medias?.length || 0,
      sorter: (a, b) => (a.medias?.length || 0) - (b.medias?.length || 0),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 120,
      render: text => text ? moment(text).format("MM/DD/YYYY hh:mm A") : "\u2014",
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
    },
  ];

  const handleClose = () => {
    setSearch("");
    setSelectedAdGroupIds([]);
    onCancel();
  };

  return (
    <Modal
      title={`Assign ${selectedAdUnits.length} Creative${selectedAdUnits.length !== 1 ? "s" : ""} to Ad Groups`}
      open={open}
      onCancel={handleClose}
      className="creative-assign-modal"
      footer={null}
      width={modalTokens.wide}
      destroyOnClose
    >
      {/* Thumbnail strip — selected creatives */}
      <div className="creative-assign-modal__thumb-strip">
        <div className="creative-assign-modal__thumb-row">
          {selectedAdUnits.map(unit => (
            <div key={unit.id} className="creative-assign-modal__thumb-chip">
              {unit.key ? (
                <MediaFetcher
                  mediaKey={unit.key}
                  className="creative-assign-modal__thumb-img"
                />
              ) : (
                <div className="creative-assign-modal__thumb-placeholder">
                  {unit.width && unit.height ? `${unit.width}x${unit.height}` : "?"}
                </div>
              )}
            </div>
          ))}
        </div>
        <span className="creative-assign-modal__thumb-count">
          {selectedAdUnits.length} creative{selectedAdUnits.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Ad groups section */}
      <div className="creative-assign-modal__section-header">
        <div className="creative-assign-modal__search-row">
          <Input
            placeholder="Search ad groups..."
            prefix={<SearchOutlined />}
            allowClear={{ clearIcon: <CloseOutlined /> }}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="creative-assign-modal__search-input"
          />
          {onCreateGroup && (
            <Button
              icon={<PlusOutlined />}
              onClick={onCreateGroup}
              className="creative-assign-modal__create-btn"
            >
              Create
            </Button>
          )}
        </div>
      </div>

      <div className="creative-table creative-assign-modal__groups-table">
        <Table
          columns={adGroupColumns}
          dataSource={filteredAdGroups}
          rowKey="id"
          size="middle"
          locale={{
            emptyText: search ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`No ad groups matching "${search}"`}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No ad groups available"
              />
            ),
          }}
          rowSelection={{
            type: "checkbox",
            columnWidth: 32,
            selectedRowKeys: selectedAdGroupIds,
            onChange: selectedRowKeys => setSelectedAdGroupIds(selectedRowKeys),
          }}
          pagination={filteredAdGroups.length > 8 ? { pageSize: 8 } : false}
        />
      </div>

      {/* CTA footer */}
      <div className="creative-assign-modal__cta-footer">
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          loading={assigning}
          disabled={selectedCount === 0 || assigning}
          onClick={onAssign}
        >
          {selectedCount > 0
            ? `Assign to ${selectedCount} Group${selectedCount !== 1 ? "s" : ""}`
            : "Select Groups to Assign"}
        </Button>
      </div>
    </Modal>
  );
};

export default AdUnitAssignmentModal;
